<template>
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
            <h3
                class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4"
            >
                System Alerts
            </h3>
            <div v-if="alerts.length === 0" class="text-center py-4">
                <CheckCircle class="mx-auto h-12 w-12 text-success-500" />
                <h3
                    class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                    All systems operational
                </h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    No alerts at this time.
                </p>
            </div>
            <div v-else class="space-y-3">
                <div
                    v-for="alert in alerts"
                    :key="alert.id"
                    :class="[
                        'p-4 rounded-lg border',
                        getAlertClasses(alert.type),
                    ]"
                >
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <component
                                :is="getAlertIcon(alert.type)"
                                :class="[
                                    'h-5 w-5',
                                    getAlertIconClasses(alert.type),
                                ]"
                                aria-hidden="true"
                            />
                        </div>
                        <div class="ml-3 flex-1">
                            <h3
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ alert.title }}
                            </h3>
                            <div
                                class="mt-1 text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ alert.description }}
                            </div>
                            <div v-if="alert.action" class="mt-3">
                                <router-link
                                    :to="alert.action.route"
                                    class="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                                >
                                    {{ alert.action.label }} →
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { AlertItem } from "@/types/dashboard";
import { TriangleAlert, CircleAlert, Info, CheckCircle } from "@lucide/vue";

interface Props {
    alerts: AlertItem[];
}

defineProps<Props>();

const getAlertClasses = (type: string) => {
    const classes = {
        warning:
            "bg-warning-50 border-warning-200 dark:bg-warning-900/20 dark:border-warning-800",
        error: "bg-danger-50 border-danger-200 dark:bg-danger-900/20 dark:border-danger-800",
        info: "bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800",
    };
    return classes[type as keyof typeof classes] || classes.info;
};

const getAlertIcon = (type: string) => {
    const icons = {
        warning: TriangleAlert,
        error: CircleAlert,
        info: Info,
    };
    return icons[type as keyof typeof icons] || Info;
};

const getAlertIconClasses = (type: string) => {
    const classes = {
        warning: "text-warning-400",
        error: "text-danger-400",
        info: "text-primary-400",
    };
    return classes[type as keyof typeof classes] || "text-primary-400";
};
</script>
